import { ExpandMore, MenuOpen } from '@mui/icons-material';
import {
    Box,
    Button,
    Menu,
    menuClasses,
    MenuItem,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';
import AvailabilityStatusIndicator from '~/components/Status/AvailabilityStatusIndicator';
import useAvailableStructureSets from '~/hooks/structure-set/useAvailableStructureSets';
import useStructureSetSelection from '~/hooks/structure-set/useStructureSetSelection';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { cutUID } from '~/utils/string';

export default function StructureSetSelection() {
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const availableStructureSets = useAvailableStructureSets(true);
    const { handleSelectLoadedStructureSet, handleSelectSessionStructureSet } =
        useStructureSetSelection({});

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(anchorEl);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <Box display="flex" flexDirection="column" gap={0.5} mt={0.5}>
            <Button
                fullWidth
                aria-controls="structure-set-menu"
                aria-haspopup="true"
                variant="outlined"
                size="small"
                onClick={handleMenuOpen}
                disabled={availableStructureSets.length <= 0}
                sx={{
                    textTransform: 'capitalize',
                    pl: 2,
                    fontSize: 12,
                }}
            >
                <MenuOpen
                    sx={{
                        position: 'absolute',
                        left: 8,
                        fontSize: 16,
                    }}
                />
                <FormattedMessage id="selectStructureSet" />
                <ExpandMore
                    sx={{
                        position: 'absolute',
                        right: 8,
                        marginLeft: 1,
                        fontSize: 16,
                    }}
                />
            </Button>
            <Menu
                id="structure-set-menu"
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                sx={{
                    mt: 0.5,
                    width: 240,
                    [`& .${menuClasses.paper}`]: {
                        width: '240px',
                    },
                }}
            >
                {availableStructureSets.map((set) => {
                    const isLoaded = set.isLoaded;
                    const isActive =
                        currentStructureSet?.dicomUid === set.sopInstanceUID;

                    return (
                        <MenuItem
                            key={set.sopInstanceUID}
                            onClick={() => {
                                if (set.structureSetObject) {
                                    handleSelectLoadedStructureSet(
                                        set.structureSetObject
                                    );
                                } else {
                                    handleSelectSessionStructureSet(
                                        set.sopInstanceUID
                                    );
                                }
                                handleMenuClose();
                            }}
                            sx={{
                                backgroundColor: isActive
                                    ? 'rgba(0, 0, 0, 0.2)'
                                    : 'inherit',
                                '&:hover': {
                                    backgroundColor: isActive
                                        ? 'rgba(0, 0, 0, 0.1)'
                                        : 'rgba(0, 0, 0, 0.3)',
                                },
                            }}
                        >
                            <Box display="flex">
                                <Box mr={1} ml={-1} width={12}>
                                    <AvailabilityStatusIndicator
                                        isLoaded={isLoaded}
                                        isActive={isActive}
                                    />
                                </Box>
                                <Box>
                                    <Typography color="white" fontSize={14}>
                                        {set.label}
                                    </Typography>
                                    <Box display="flex" gap={0.5}>
                                        <Typography
                                            variant="body2"
                                            fontSize={12}
                                            color={'textSecondary'}
                                        >
                                            {isLoaded ? 'Loaded' : 'Not Loaded'}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontSize={12}
                                            color="textSecondary"
                                        >
                                            -{' '}
                                            {cutUID(
                                                set.sopInstanceUID,
                                                4,
                                                6
                                            )}{' '}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </MenuItem>
                    );
                })}
            </Menu>
        </Box>
    );
}
